import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { FiCheck, FiCopy, FiLink, FiTrash2, FiX } from 'react-icons/fi';
import {
  PipelineShareLink,
  Pipeline,
  PipelineSharePermission,
  PipelineCollaborator,
  getPipelineShareUrl,
} from '../../types/fundraise';

const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;
const CopyIcon = FiCopy as React.FC<React.SVGProps<SVGSVGElement>>;
const CheckIcon = FiCheck as React.FC<React.SVGProps<SVGSVGElement>>;
const LinkIcon = FiLink as React.FC<React.SVGProps<SVGSVGElement>>;
const TrashIcon = FiTrash2 as React.FC<React.SVGProps<SVGSVGElement>>;

interface PipelineShareModalProps {
  pipeline: Pipeline;
  shareLinks: PipelineShareLink[];
  collaborators: PipelineCollaborator[];
  onClose: () => void;
  onCreateLink: (permission: PipelineSharePermission) => Promise<PipelineShareLink>;
  onToggleLink: (linkId: string, isActive: boolean) => Promise<void>;
  onDeleteLink: (linkId: string) => Promise<void>;
  onRemoveCollaborator: (collaboratorUserId: string) => Promise<void>;
}

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const PipelineShareModal: React.FC<PipelineShareModalProps> = ({
  pipeline,
  shareLinks,
  collaborators,
  onClose,
  onCreateLink,
  onToggleLink,
  onDeleteLink,
  onRemoveCollaborator,
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const [permission, setPermission] = useState<PipelineSharePermission>('view');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isCreating) onClose();
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isCreating) onClose();
    },
    [isCreating, onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleCreate = async () => {
    setError(null);
    setIsCreating(true);
    try {
      await onCreateLink(permission);
    } catch (err: any) {
      setError(err?.message || 'Failed to create share link');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopy = async (link: PipelineShareLink) => {
    const url = getPipelineShareUrl(link.url_slug || link.share_token, pipeline.name);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(link.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      setError('Failed to copy link to clipboard');
    }
  };

  const handleToggle = async (link: PipelineShareLink) => {
    setError(null);
    try {
      await onToggleLink(link.id, !link.is_active);
    } catch (err: any) {
      setError(err?.message || 'Failed to update link');
    }
  };

  const handleDelete = async (linkId: string) => {
    setError(null);
    try {
      await onDeleteLink(linkId);
    } catch (err: any) {
      setError(err?.message || 'Failed to delete link');
    }
  };

  const handleRevoke = async (collaboratorUserId: string) => {
    setError(null);
    try {
      await onRemoveCollaborator(collaboratorUserId);
    } catch (err: any) {
      setError(err?.message || 'Failed to remove collaborator');
    }
  };

  const modal = (
    <div className="su-fundraise-modal-overlay" onClick={handleOverlayClick}>
      <div
        className="su-fundraise-modal"
        style={{ maxWidth: 600 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pipeline-share-title"
      >
        <div className="su-fundraise-modal-header">
          <div>
            <h2 id="pipeline-share-title" className="su-fundraise-modal-title">
              Share Pipeline
            </h2>
            <p className="su-fundraise-modal-subtitle">
              Create and manage share links for {pipeline.name}
            </p>
          </div>
          <button
            type="button"
            className="su-fundraise-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            <CloseIcon width={20} height={20} />
          </button>
        </div>

        <div className="su-fundraise-modal-body">
          {error && <div className="su-fundraise-error-message">{error}</div>}

          {/* Permission picker */}
          <div className="su-fundraise-form-group">
            <div className="su-fundraise-label">Link permission</div>
            <div className="su-fundraise-permission-picker">
              <button
                type="button"
                className={`su-fundraise-permission-option ${permission === 'view' ? 'selected' : ''}`}
                onClick={() => setPermission('view')}
              >
                <div className="su-fundraise-permission-title">View only</div>
                <div className="su-fundraise-permission-desc">
                  Anyone with the link can see the pipeline read-only. No account needed.
                </div>
              </button>
              <button
                type="button"
                className={`su-fundraise-permission-option ${permission === 'collaborate' ? 'selected' : ''}`}
                onClick={() => setPermission('collaborate')}
              >
                <div className="su-fundraise-permission-title">Collaborate</div>
                <div className="su-fundraise-permission-desc">
                  Recipient signs in, then the pipeline appears in their list. They can
                  edit funds but cannot delete or re-share.
                </div>
              </button>
            </div>
          </div>

          <button
            type="button"
            className="su-btn-primary"
            onClick={handleCreate}
            disabled={isCreating}
            style={{ width: '100%', marginBottom: 20 }}
          >
            <LinkIcon width={16} height={16} />
            <span style={{ marginLeft: 8 }}>
              {isCreating ? 'Creating...' : `Create ${permission === 'collaborate' ? 'Collaborate' : 'View'} Link`}
            </span>
          </button>

          {/* Existing links */}
          <div className="su-fundraise-label" style={{ marginBottom: 12 }}>
            Existing links
          </div>
          {shareLinks.length === 0 ? (
            <div className="su-fundraise-share-empty">
              <LinkIcon style={{ width: 32, height: 32, marginBottom: 12, opacity: 0.5 }} />
              <p style={{ margin: 0 }}>No share links yet</p>
              <p style={{ margin: '8px 0 0', fontSize: 13, opacity: 0.7 }}>
                Pick a permission above and create your first link.
              </p>
            </div>
          ) : (
            <div className="su-fundraise-share-list">
              {shareLinks.map((link) => {
                const url = getPipelineShareUrl(
                  link.url_slug || link.share_token,
                  pipeline.name
                );
                return (
                  <div
                    key={link.id}
                    className={`su-fundraise-share-item ${!link.is_active ? 'inactive' : ''}`}
                  >
                    <div className="su-fundraise-share-info">
                      <div className="su-fundraise-share-url" title={url}>
                        {url}
                      </div>
                      <div className="su-fundraise-share-meta">
                        <span
                          className={
                            link.is_active
                              ? 'su-fundraise-share-badge active'
                              : 'su-fundraise-share-badge inactive'
                          }
                        >
                          {link.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <span className="su-fundraise-share-meta-dot">·</span>
                        <span className="su-fundraise-share-badge permission">
                          {link.permission_level === 'collaborate' ? 'Collaborate' : 'View only'}
                        </span>
                        <span className="su-fundraise-share-meta-dot">·</span>
                        <span>Created {formatDate(link.created_at)}</span>
                      </div>
                    </div>
                    <div className="su-fundraise-share-actions">
                      <button
                        type="button"
                        className="su-fundraise-share-action"
                        onClick={() => handleCopy(link)}
                        title="Copy link"
                        aria-label="Copy share link to clipboard"
                      >
                        {copiedId === link.id ? (
                          <CheckIcon width={16} height={16} />
                        ) : (
                          <CopyIcon width={16} height={16} />
                        )}
                      </button>
                      <button
                        type="button"
                        className="su-fundraise-share-action"
                        onClick={() => handleToggle(link)}
                        title={link.is_active ? 'Deactivate' : 'Reactivate'}
                      >
                        {link.is_active ? 'Pause' : 'Resume'}
                      </button>
                      <button
                        type="button"
                        className="su-fundraise-share-action delete"
                        onClick={() => handleDelete(link.id)}
                        title="Delete link"
                        aria-label="Delete share link"
                      >
                        <TrashIcon width={16} height={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Collaborators */}
          {collaborators.length > 0 && (
            <>
              <div
                className="su-fundraise-label"
                style={{ marginTop: 24, marginBottom: 12 }}
              >
                Collaborators
              </div>
              <div className="su-fundraise-share-list">
                {collaborators.map((c) => {
                  const label = c.full_name || c.email || c.user_id;
                  return (
                    <div key={c.id} className="su-fundraise-share-item">
                      <div className="su-fundraise-share-info">
                        <div className="su-fundraise-share-url" title={label}>
                          {label}
                        </div>
                        <div className="su-fundraise-share-meta">
                          {c.email && c.full_name && (
                            <>
                              <span>{c.email}</span>
                              <span className="su-fundraise-share-meta-dot">·</span>
                            </>
                          )}
                          <span>Joined {formatDate(c.joined_at)}</span>
                        </div>
                      </div>
                      <div className="su-fundraise-share-actions">
                        <button
                          type="button"
                          className="su-fundraise-share-action delete"
                          onClick={() => handleRevoke(c.user_id)}
                          title="Remove collaborator"
                          aria-label="Remove collaborator"
                        >
                          <TrashIcon width={16} height={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="su-fundraise-modal-footer">
          <button type="button" className="su-btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default PipelineShareModal;
